/**
 * AdvancePaymentTab - Onglet Acomptes Récurrents
 * Configuration et historique des acomptes pour un salarié
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { ScrollArea } from "../../ui/scroll-area";
import { Separator } from "../../ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { 
  Wallet, Calendar, Check, X, AlertCircle, Loader2,
  Settings, History, Euro, CalendarCheck, SkipForward,
  Bell, Mail, CheckCircle2, Clock, XCircle
} from "lucide-react";
import { toast } from "sonner";
import AdvancePaymentSettingsDialog from "../AdvancePaymentSettingsDialog";

const API = process.env.REACT_APP_BACKEND_URL;

// French months
const FRENCH_MONTHS = {
  1: "Janvier", 2: "Février", 3: "Mars", 4: "Avril",
  5: "Mai", 6: "Juin", 7: "Juillet", 8: "Août",
  9: "Septembre", 10: "Octobre", 11: "Novembre", 12: "Décembre"
};

// Status badges
const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-amber-100 text-amber-700", icon: Clock },
  paid: { label: "Payé", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  skipped: { label: "Passé", color: "bg-slate-100 text-slate-500", icon: SkipForward },
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  } catch {
    return dateStr;
  }
};

export default function AdvancePaymentTab({ employeeId, token, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    amount: "",
    day_of_month: 15,
    is_active: true
  });
  const [hasConfig, setHasConfig] = useState(false);
  
  // Dialogs
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [showEmailHistory, setShowEmailHistory] = useState(false);
  const [emailLogs, setEmailLogs] = useState({ logs: [], stats: null, loading: false });
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);

  const fetchEmailLogs = useCallback(async () => {
    if (!employeeId || !token) return;
    setEmailLogs((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.get(
        `${API}/api/hr/advance-payments/email-logs?employee_id=${employeeId}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailLogs({ logs: res.data.logs || [], stats: res.data.stats, loading: false });
    } catch (e) {
      setEmailLogs({ logs: [], stats: null, loading: false });
      toast.error("Impossible de charger l'historique des emails");
    }
  }, [employeeId, token]);

  // Send manual reminder
  const handleSendReminder = async (payment, reminderType) => {
    setSendingReminder(payment.id);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employeeId}/advance-payments/${payment.id}/send-reminder?reminder_type=${reminderType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email de rappel (${reminderType.toUpperCase()}) envoyé avec succès`);
      fetchData(); // Refresh to show updated status
    } catch (err) {
      console.error("Error sending reminder:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du rappel");
    } finally {
      setSendingReminder(null);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!employeeId || !token) return;
    
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/hr/employees/${employeeId}/advance-payments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
      
      if (res.data.config) {
        setHasConfig(true);
        setConfigForm({
          amount: res.data.config.amount?.toString() || "",
          day_of_month: res.data.config.day_of_month || 15,
          is_active: res.data.config.is_active ?? true
        });
      } else {
        setHasConfig(false);
      }
    } catch (err) {
      console.error("Error fetching advance payments:", err);
      toast.error("Erreur lors du chargement des acomptes");
    } finally {
      setLoading(false);
    }
  }, [employeeId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save config
  const saveConfig = async () => {
    if (!configForm.amount || parseFloat(configForm.amount) <= 0) {
      toast.error("Veuillez saisir un montant valide");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employeeId}/advance-config`,
        {
          amount: parseFloat(configForm.amount),
          day_of_month: parseInt(configForm.day_of_month),
          is_active: configForm.is_active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Configuration enregistrée");
      setShowConfigDialog(false);
      fetchData();
      onUpdate?.();
    } catch (err) {
      console.error("Error saving config:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Delete config
  const deleteConfig = async () => {
    setActionLoading(true);
    try {
      await axios.delete(
        `${API}/api/hr/employees/${employeeId}/advance-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Configuration supprimée");
      setConfirmAction(null);
      setConfigForm({ amount: "", day_of_month: 15, is_active: true });
      setHasConfig(false);
      fetchData();
      onUpdate?.();
    } catch (err) {
      console.error("Error deleting config:", err);
      toast.error("Erreur lors de la suppression");
    } finally {
      setActionLoading(false);
    }
  };

  // Mark payment as paid
  const markAsPaid = async (paymentId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employeeId}/advance-payments/${paymentId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Acompte marqué comme payé et ajouté aux variables de paie");
      setConfirmAction(null);
      fetchData();
      onUpdate?.();
    } catch (err) {
      console.error("Error marking as paid:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setActionLoading(false);
    }
  };

  // Skip payment
  const skipPayment = async (paymentId) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employeeId}/advance-payments/${paymentId}/skip`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Acompte passé pour ce mois");
      setConfirmAction(null);
      fetchData();
    } catch (err) {
      console.error("Error skipping payment:", err);
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6">
      {/* Email Settings Button */}
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowEmailSettings(true)}
          className="flex items-center gap-2"
        >
          <Mail className="h-4 w-4" />
          Paramètres des emails
        </Button>
      </div>

      {/* Configuration Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Configuration de l'acompte
            </CardTitle>
            <CardDescription>
              Paramétrez l'acompte récurrent mensuel pour ce salarié
            </CardDescription>
          </div>
          <Button onClick={() => setShowConfigDialog(true)}>
            {hasConfig ? "Modifier" : "Configurer"}
          </Button>
        </CardHeader>
        <CardContent>
          {hasConfig && data?.config ? (
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Montant mensuel</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(data.config.amount)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Jour du virement</p>
                <p className="text-2xl font-bold text-slate-900">
                  Le {data.config.day_of_month}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Statut</p>
                <Badge className={data.config.is_active 
                  ? "bg-emerald-100 text-emerald-700" 
                  : "bg-slate-100 text-slate-500"
                }>
                  {data.config.is_active ? "Actif" : "Suspendu"}
                </Badge>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Wallet className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Aucun acompte configuré pour ce salarié</p>
              <p className="text-sm">Cliquez sur "Configurer" pour définir un acompte récurrent</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Card */}
      {data && (data.total_paid_this_year > 0 || data.total_paid_all_time > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Euro className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total {currentYear}</p>
                  <p className="text-2xl font-bold">{formatCurrency(data.total_paid_this_year)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 p-3 rounded-full">
                  <History className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total historique</p>
                  <p className="text-2xl font-bold">{formatCurrency(data.total_paid_all_time)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Historique des acomptes
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowEmailHistory(true);
                fetchEmailLogs();
              }}
              data-testid="show-email-history-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Emails envoyés
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {data?.payments?.length > 0 ? (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Période</TableHead>
                    <TableHead>Échéance</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Rappels</TableHead>
                    <TableHead>Variables paie</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.payments.map((payment) => {
                    const StatusIcon = STATUS_CONFIG[payment.status]?.icon || Clock;
                    return (
                      <TableRow key={payment.id}>
                        <TableCell className="font-medium">
                          {FRENCH_MONTHS[payment.month]} {payment.year}
                        </TableCell>
                        <TableCell>{formatDate(payment.due_date)}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_CONFIG[payment.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[payment.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 items-center flex-wrap">
                            {payment.reminder_j2_sent && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">J-2 ✓</Badge>
                            )}
                            {payment.reminder_j1_sent && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">J-1 ✓</Badge>
                            )}
                            {payment.reminder_j0_sent && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">J0 ✓</Badge>
                            )}
                            {!payment.reminder_j2_sent && !payment.reminder_j1_sent && !payment.reminder_j0_sent && (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {payment.injected_to_payroll ? (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <Check className="h-3 w-3 mr-1" />
                              Injecté
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {payment.status === "pending" && (
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => setConfirmAction({ type: 'pay', payment })}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Payé
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-500"
                                onClick={() => setConfirmAction({ type: 'skip', payment })}
                              >
                                <SkipForward className="h-4 w-4 mr-1" />
                                Passer
                              </Button>
                            </div>
                          )}
                          {payment.status === "paid" && payment.paid_at && (
                            <span className="text-xs text-slate-500">
                              {formatDate(payment.paid_at)}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Aucun historique d'acompte</p>
              {hasConfig && (
                <p className="text-sm mt-2">
                  Les acomptes seront générés automatiquement le 1er de chaque mois
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Configuration de l'acompte</DialogTitle>
            <DialogDescription>
              Définissez le montant et la date de virement récurrent
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Montant mensuel (€)</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="500.00"
                  className="pl-9"
                  value={configForm.amount}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="day">Jour du mois pour le virement</Label>
              <Select
                value={configForm.day_of_month.toString()}
                onValueChange={(v) => setConfigForm(prev => ({ ...prev, day_of_month: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un jour" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={day.toString()}>
                      Le {day} du mois
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Les rappels seront envoyés J-1 et le jour même
              </p>
            </div>
            
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>Activer l'acompte</Label>
                <p className="text-sm text-slate-500">
                  Génère automatiquement l'acompte chaque mois
                </p>
              </div>
              <Switch
                checked={configForm.is_active}
                onCheckedChange={(checked) => setConfigForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            {hasConfig && (
              <Button
                variant="destructive"
                onClick={() => {
                  setShowConfigDialog(false);
                  setConfirmAction({ type: 'delete' });
                }}
              >
                Supprimer
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Annuler
              </Button>
              <Button onClick={saveConfig} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enregistrer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'pay' && "Confirmer le paiement"}
              {confirmAction?.type === 'skip' && "Passer cet acompte"}
              {confirmAction?.type === 'delete' && "Supprimer la configuration"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === 'pay' && (
                <>
                  Confirmez-vous avoir effectué le virement de{" "}
                  <strong>{formatCurrency(confirmAction.payment?.amount)}</strong> ?
                  <br />
                  <span className="text-emerald-600">
                    Le montant sera automatiquement ajouté aux variables de paie.
                  </span>
                </>
              )}
              {confirmAction?.type === 'skip' && (
                <>
                  L'acompte de {formatCurrency(confirmAction.payment?.amount)} pour{" "}
                  {FRENCH_MONTHS[confirmAction.payment?.month]} {confirmAction.payment?.year}{" "}
                  sera marqué comme passé et ne sera pas injecté dans les variables de paie.
                </>
              )}
              {confirmAction?.type === 'delete' && (
                <>
                  Cette action supprimera la configuration d'acompte.
                  Les acomptes en cours ne seront pas supprimés.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmAction?.type === 'pay') markAsPaid(confirmAction.payment.id);
                else if (confirmAction?.type === 'skip') skipPayment(confirmAction.payment.id);
                else if (confirmAction?.type === 'delete') deleteConfig();
              }}
              disabled={actionLoading}
              className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirmAction?.type === 'pay' && "Confirmer le paiement"}
              {confirmAction?.type === 'skip' && "Passer"}
              {confirmAction?.type === 'delete' && "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Settings Dialog */}
      <AdvancePaymentSettingsDialog
        open={showEmailSettings}
        onOpenChange={setShowEmailSettings}
        token={token}
      />

      {/* Email History Dialog */}
      <Dialog open={showEmailHistory} onOpenChange={setShowEmailHistory}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="email-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Historique des emails d&apos;acompte
            </DialogTitle>
            <DialogDescription>
              {emailLogs.stats && (
                <span className="flex gap-3 mt-1 text-xs">
                  <Badge className="bg-slate-100 text-slate-700">{emailLogs.stats.total} email(s)</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700">{emailLogs.stats.sent} envoyé(s)</Badge>
                  {emailLogs.stats.failed > 0 && (
                    <Badge className="bg-red-100 text-red-700">{emailLogs.stats.failed} échec(s)</Badge>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {emailLogs.loading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : emailLogs.logs.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">
                <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Aucun email envoyé pour ce salarié pour l&apos;instant.
              </div>
            ) : (
              <div className="divide-y">
                {emailLogs.logs.map((log, idx) => (
                  <div key={idx} className="py-3 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {log.status === 'sent' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">{log.type}</Badge>
                        <span className="font-medium text-sm text-slate-900">{formatCurrency(log.amount)}</span>
                        {log.due_date && (
                          <span className="text-xs text-slate-500">échéance {formatDate(log.due_date)}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        → {log.recipient}
                        {log.cc?.length > 0 && ` (CC : ${log.cc.join(', ')})`}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {log.sent_at && new Date(log.sent_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                      {log.status === 'failed' && log.error && (
                        <div className="text-xs text-red-600 mt-1 font-mono">⚠ {log.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
